import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, fmtINR, fmtMeters, fmtRate, fmtDate } from "../lib/api";
import { PlusCircle, Search, Package, Download, ClipboardList, FileText, FileCheck, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";

const Pill = ({ status }) => {
  const map = {
    delivered: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    partial: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
    pending: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
    paid: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    unpaid: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
    cancelled: "bg-[#F3F4F6] text-[#4B5563] border-[#D1D5DB] line-through",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${map[status] || ""}`}>{status}</span>
  );
};

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", buyer: "", seller: "", category: "", status: "all" });
  const [buyers, setBuyers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [handlers, setHandlers] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [gExporting, setGExporting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";

  const [reportOpen, setReportOpen] = useState(false);
  const [monthFilter, setMonthFilter] = useState(() => new URLSearchParams(window.location.search).get("month") || "");
  const [rf, setRf] = useState({ type: "payments", buyer: "", seller: "", category: "", status: "unpaid", group_by: "default", handler: "", city: "", by: "buyer" });
  const [busy, setBusy] = useState("");
  const [consol, setConsol] = useState(null);

  const blobDownload = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleClose = async (e, o, kind) => {
    e.stopPropagation();
    const ep = kind === "delivery" ? "close-delivery" : "close-payment";
    const key = kind === "delivery" ? "delivery_closed" : "payment_closed";
    try {
      const { data } = await api.post(`/orders/${o.id}/${ep}`, { [key]: !o[key] });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? data : x)));
      toast.success(kind === "delivery"
        ? (data.delivery_closed ? "Marked delivered." : "Delivery reopened.")
        : (data.payment_closed ? "Marked payment received." : "Payment reopened."));
    } catch { toast.error("Failed to update."); }
  };

  const setType = (t) => setRf({ ...rf, type: t, status: t === "payments" ? "unpaid" : "pending_all" });

  const consolParams = () => {
    const p = { by: rf.by };
    if (rf.handler) p.handler = rf.handler;
    if (rf.city) p.city = rf.city;
    return p;
  };

  const viewConsolidated = async () => {
    setBusy("view");
    try {
      const { data } = await api.get("/reports/consolidated", { params: consolParams() });
      setConsol(data);
      setReportOpen(false);
    } catch { toast.error("Could not load consolidated report."); }
    finally { setBusy(""); }
  };

  const downloadConsolidated = async () => {
    setBusy("consoldl");
    try {
      const res = await api.get("/reports/consolidated/pdf", { params: consolParams(), responseType: "blob" });
      blobDownload(res.data, `consolidated-outstanding-${rf.by}.pdf`);
    } catch { toast.error("Could not generate consolidated PDF."); }
    finally { setBusy(""); }
  };

  const reportParams = () => {
    const p = { type: rf.type, status: rf.status };
    if (rf.buyer) p.buyer = rf.buyer;
    if (rf.seller) p.seller = rf.seller;
    if (rf.type === "deliveries" && rf.category) p.category = rf.category;
    if (rf.group_by && rf.group_by !== "default") p.group_by = rf.group_by;
    if (rf.handler) p.handler = rf.handler;
    if (rf.city) p.city = rf.city;
    return p;
  };

  const downloadReport = async () => {
    setBusy("dl");
    try {
      const res = await api.get("/reports/pdf", { params: reportParams(), responseType: "blob" });
      blobDownload(res.data, `${rf.type}-report-${rf.status}.pdf`);
    } catch { toast.error("Could not generate report."); }
    finally { setBusy(""); }
  };

  const emailReport = async () => {
    setBusy("email");
    try {
      await api.post("/reports/send", reportParams());
      toast.success("Report emailed to you.");
      setReportOpen(false);
    } catch { toast.error("Email failed."); }
    finally { setBusy(""); }
  };

  const downloadClosed = async () => {
    try {
      const res = await api.get("/reports/closed-orders/pdf", { responseType: "blob" });
      blobDownload(res.data, "closed-orders.pdf");
    } catch { toast.error("Could not generate."); }
  };

  const downloadCommission = async () => {
    try {
      const res = await api.get("/reports/commission/pdf", { responseType: "blob" });
      blobDownload(res.data, "commission-report.pdf");
    } catch { toast.error("Could not generate commission report."); }
  };

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/parties").then((r) => {
      setBuyers(r.data.filter((p) => p.role === "buyer").map((p) => p.name));
      setSellers(r.data.filter((p) => p.role === "seller").map((p) => p.name));
    }).catch(() => {});
    api.get("/seller-categories").then((r) => setCategories(r.data.category_list || [])).catch(() => {});
    api.get("/buyer-meta").then((r) => { setHandlers(r.data.handlers || []); setCities(r.data.cities || []); }).catch(() => {});
    api.get("/google/status").then((r) => setGoogleConnected(!!r.data.connected)).catch(() => {});
    const p = new URLSearchParams(window.location.search).get("google");
    if (p === "connected") { toast.success("Google account connected."); setGoogleConnected(true); setExportOpen(true); }
    else if (p === "error") { toast.error("Google connection failed."); }
  }, []);

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/google/auth-url");
      window.location.href = data.url;
    } catch { toast.error("Could not start Google connection."); }
  };

  const exportToSheets = async () => {
    setGExporting(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && !(k === "status" && v === "all")) params[k] = v; });
      const { data } = await api.get("/google/export-sheet", { params, method: "post" });
      window.open(data.url, "_blank");
      toast.success("Google Sheet created.");
    } catch (e) {
      toast.error("Sheets export failed.");
    } finally { setGExporting(false); }
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && !(k === "status" && v === "all")) params[k] = v; });
      const res = await api.get("/export/excel", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "madhusudan-expofab-export.xlsx"; a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } finally {
      setExporting(false);
    }
  };

  if (!orders) return <div className="text-slate-400 font-mono text-sm">Loading orders…</div>;

  const filtered = orders.filter((o) => {
    const matchesText = [o.buyer_name, o.seller_name, o.po_number, o.fabric_type].join(" ").toLowerCase().includes(q.toLowerCase());
    const omonth = (o.po_date || o.created_at || "").slice(0, 7);
    const matchesMonth = !monthFilter || omonth === monthFilter;
    if (status === "cancelled") return matchesText && matchesMonth && !!o.cancelled;
    if (o.cancelled) return false;
    let matchesStatus = true;
    if (status === "overdue") matchesStatus = !!o.overdue;
    else if (status === "pending_delivery") matchesStatus = o.delivery_status !== "delivered";
    else if (status === "pending_payment") matchesStatus = o.payment_status !== "paid";
    else if (status === "delivered") matchesStatus = o.delivery_status === "delivered";
    else if (status === "paid") matchesStatus = o.payment_status === "paid";
    return matchesText && matchesMonth && matchesStatus;
  });

  const monthLabel = monthFilter ? new Date(`${monthFilter}-01T00:00:00`).toLocaleString("en-US", { month: "short", year: "numeric" }) : "";
  const monthOrders = monthFilter ? orders.filter((o) => (o.po_date || o.created_at || "").slice(0, 7) === monthFilter && !o.cancelled) : [];
  const monthTotal = monthOrders.reduce((s, o) => s + (Number(o.total_amount) || 0), 0);
  const clearMonth = () => {
    setMonthFilter("");
    window.history.replaceState(null, "", "/orders");
  };

  const STATUS_TABS = [
    ["all", "All"],
    ["overdue", "Overdue"],
    ["pending_delivery", "Pending Delivery"],
    ["pending_payment", "Pending Payment"],
    ["delivered", "Delivered"],
    ["paid", "Paid"],
    ["cancelled", "Cancelled"],
  ];

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Tracking</p>
          <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1">Orders</h1>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={exportOpen} onOpenChange={setExportOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="export-excel-btn"
                className="bg-white text-[#1C2331] border border-[#E6E4DD] rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:border-[#1C2331] transition-colors"
              >
                <Download className="w-4 h-4" /> Export
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Export data (Excel)</DialogTitle>
                <DialogDescription className="sr-only">Apply filters before generating the export</DialogDescription>
              </DialogHeader>
              <p className="text-xs text-[#8CA3A7] -mt-2">Two sheets — Purchase Order Details & Invoice Details. Opens in Google Sheets. Leave blank for everything.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">PO Date from</label>
                  <input data-testid="export-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">PO Date to</label>
                  <input data-testid="export-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Buyer</label>
                  <input data-testid="export-buyer" list="exp-buyers" value={filters.buyer} onChange={(e) => setFilters({ ...filters, buyer: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Seller</label>
                  <input data-testid="export-seller" list="exp-sellers" value={filters.seller} onChange={(e) => setFilters({ ...filters, seller: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Category Seller</label>
                  <input data-testid="export-category" list="exp-cats" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Status</label>
                  <select data-testid="export-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                    <option value="all">All</option>
                    <option value="overdue">Overdue</option>
                    <option value="pending_delivery">Pending Delivery</option>
                    <option value="pending_payment">Pending Payment</option>
                    <option value="delivered">Delivered</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
              <datalist id="exp-buyers">{buyers.map((b) => <option key={b} value={b} />)}</datalist>
              <datalist id="exp-sellers">{sellers.map((s) => <option key={s} value={s} />)}</datalist>
              <datalist id="exp-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              <button data-testid="generate-export-btn" onClick={runExport} disabled={exporting} className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
                {exporting ? "Generating…" : "Download Excel"}
              </button>
              {googleConnected ? (
                <button data-testid="export-sheets-btn" onClick={exportToSheets} disabled={gExporting} className="w-full mt-2 bg-[#065F46] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#054d39] transition-colors disabled:opacity-60">
                  {gExporting ? "Creating sheet…" : "Export to Google Sheets"}
                </button>
              ) : (
                <button data-testid="connect-google-btn" onClick={connectGoogle} className="w-full mt-2 bg-white text-[#1C2331] border border-[#E6E4DD] rounded-sm px-4 py-2.5 text-sm font-medium hover:border-[#1C2331] transition-colors">
                  Connect Google to export to Sheets
                </button>
              )}
            </DialogContent>
          </Dialog>
          <button
            data-testid="closed-orders-btn"
            onClick={downloadClosed}
            className="bg-white text-[#1C2331] border border-[#E6E4DD] rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:border-[#1C2331] transition-colors"
          >
            <FileCheck className="w-4 h-4" /> Closed PDF
          </button>
          <button
            data-testid="commission-report-btn"
            onClick={downloadCommission}
            className="bg-white text-[#1C2331] border border-[#E6E4DD] rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:border-[#1C2331] transition-colors"
          >
            <FileText className="w-4 h-4" /> Commission PDF
          </button>
          <button
            data-testid="open-report-btn"
            onClick={() => setReportOpen(true)}
            className="bg-[#D95D39] text-white rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-[#c24e2e] transition-colors"
          >
            <FileText className="w-4 h-4" /> Report
          </button>
          {canEdit && (
          <button
            data-testid="new-order-btn"
            onClick={() => navigate("/orders/new")}
            className="bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-transform active:scale-95 hover:bg-[#2A3447]"
          >
            <PlusCircle className="w-4 h-4" /> New Order
          </button>
          )}
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate report</DialogTitle>
            <DialogDescription className="sr-only">Choose report type and filters</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            {[["payments", "Payments"], ["deliveries", "Deliveries"], ["consolidated", "Consolidated"]].map(([v, l]) => (
              <button key={v} data-testid={`report-type-${v}`} onClick={() => setType(v)}
                className={`flex-1 text-sm px-3 py-2 rounded-sm border transition-colors ${rf.type === v ? "bg-[#1C2331] text-white border-[#1C2331]" : "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]"}`}>{l}</button>
            ))}
          </div>
          <p className="text-xs text-[#8CA3A7] -mt-1">
            {rf.type === "payments"
              ? "Grouped Buyer → Seller. Columns: PO Date, Buyer P.O., Invoice, Invoice Date, Qty, Payment Due On, Amount, Bill Days, Due Days."
              : rf.type === "deliveries"
              ? "Grouped Seller → Buyer. Pending / delivered deliveries per order."
              : "Outstanding summary. Pick Buyer or Seller — see each counterparty's Payments Overdue, Not Yet Due and Total, with subtotals and a grand total."}
          </p>
          {rf.type === "consolidated" ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">View by</label>
                <div className="flex gap-2 mt-1">
                  {[["buyer", "By Buyer"], ["seller", "By Seller"]].map(([v, l]) => (
                    <button key={v} data-testid={`consol-by-${v}`} onClick={() => setRf({ ...rf, by: v })}
                      className={`flex-1 text-sm px-3 py-2 rounded-sm border transition-colors ${rf.by === v ? "bg-[#D95D39] text-white border-[#D95D39]" : "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]"}`}>{l}</button>
                  ))}
                </div>
                <p className="text-[11px] text-[#8CA3A7] mt-1">
                  {rf.by === "buyer" ? "Each buyer, then their sellers." : "Each seller, then their buyers."}
                </p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">City</label>
                <select data-testid="consol-city" value={rf.city} onChange={(e) => setRf({ ...rf, city: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                  <option value="">All cities</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Handled By</label>
                <select data-testid="consol-handler" value={rf.handler} onChange={(e) => setRf({ ...rf, handler: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                  <option value="">All handlers</option>
                  {handlers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Buyer</label>
              <input data-testid="report-buyer" list="rep-buyers" value={rf.buyer} onChange={(e) => setRf({ ...rf, buyer: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Seller</label>
              <input data-testid="report-seller" list="rep-sellers" value={rf.seller} onChange={(e) => setRf({ ...rf, seller: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
            </div>
            {rf.type === "deliveries" && (
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Category Seller</label>
                <input data-testid="report-category" list="rep-cats" value={rf.category} onChange={(e) => setRf({ ...rf, category: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
              </div>
            )}
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Show</label>
              <select data-testid="report-status" value={rf.status} onChange={(e) => setRf({ ...rf, status: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                {rf.type === "payments" ? (
                  <>
                    <option value="all">All (overdue + unpaid + paid)</option>
                    <option value="overdue">Overdue only</option>
                    <option value="unpaid">Unpaid (pending)</option>
                    <option value="paid">Paid</option>
                  </>
                ) : (
                  <>
                    <option value="all">All (pending + delivered)</option>
                    <option value="pending_all">Pending — all</option>
                    <option value="pending_7">Pending — next 7 days</option>
                    <option value="delivered">Delivered</option>
                  </>
                )}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">City</label>
              <select data-testid="report-city" value={rf.city} onChange={(e) => setRf({ ...rf, city: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                <option value="">All cities</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Handled By</label>
              <select data-testid="report-handler" value={rf.handler} onChange={(e) => setRf({ ...rf, handler: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                <option value="">All handlers</option>
                {handlers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Segregate by</label>
              <select data-testid="report-groupby" value={rf.group_by} onChange={(e) => setRf({ ...rf, group_by: e.target.value })} className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#1C2331]">
                <option value="default">{rf.type === "payments" ? "Buyer → Seller (default)" : "Seller → Buyer (default)"}</option>
                <option value="city">City → Buyer</option>
                <option value="handler">Handled By → Buyer</option>
              </select>
            </div>
          </div>
          )}
          <datalist id="rep-buyers">{buyers.map((b) => <option key={b} value={b} />)}</datalist>
          <datalist id="rep-sellers">{sellers.map((s) => <option key={s} value={s} />)}</datalist>
          <datalist id="rep-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          {rf.type === "consolidated" ? (
          <div className="flex gap-2">
            <button data-testid="view-consolidated-btn" onClick={viewConsolidated} disabled={busy === "view"} className="flex-1 bg-[#D95D39] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#c24e2e] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              <ClipboardList className="w-4 h-4" /> {busy === "view" ? "Loading…" : "View on screen"}
            </button>
            <button data-testid="download-consolidated-btn" onClick={downloadConsolidated} disabled={busy === "consoldl"} className="flex-1 bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> {busy === "consoldl" ? "Generating…" : "Download PDF"}
            </button>
          </div>
          ) : (
          <div className="flex gap-2">
            <button data-testid="download-report-btn" onClick={downloadReport} disabled={busy === "dl"} className="flex-1 bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> {busy === "dl" ? "Generating…" : "Download PDF"}
            </button>
            <button data-testid="email-report-btn" onClick={emailReport} disabled={busy === "email"} className="flex-1 bg-[#065F46] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#054d39] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              <Mail className="w-4 h-4" /> {busy === "email" ? "Sending…" : "Email me"}
            </button>
          </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="flex flex-col gap-3">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-[#8CA3A7] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search buyer, seller, PO#, fabric…"
            className="w-full rounded-sm border border-[#E6E4DD] bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331] focus:border-[#1C2331]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_TABS.map(([v, l]) => (
            <button
              key={v}
              data-testid={`orders-filter-${v}`}
              onClick={() => setStatus(v)}
              className={`text-sm px-3 py-1.5 rounded-sm border transition-colors ${status === v ? "bg-[#1C2331] text-white border-[#1C2331]" : "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]"}`}
            >
              {l}
            </button>
          ))}
          <span className="text-xs text-[#8CA3A7] font-mono ml-1">{filtered.length} shown</span>
          {monthFilter && (
            <button
              data-testid="month-filter-chip"
              onClick={clearMonth}
              className="text-xs font-mono px-2.5 py-1 rounded-sm border border-[#D95D39] bg-[#FFF4EF] text-[#D95D39] flex items-center gap-1.5 hover:bg-[#FDE9E1] transition-colors"
            >
              {monthLabel} <span className="text-[#991B1B] font-bold">×</span>
            </button>
          )}
        </div>
      </div>

      {monthFilter && (
        <div data-testid="month-total-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-[#D95D39]/40 bg-[#FFF4EF] px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#D95D39]">{monthLabel} · Order Value</p>
            <p className="font-heading text-2xl font-bold text-[#0A0D14] tabular-nums mt-1">{fmtINR(monthTotal)}</p>
            <p className="text-xs text-[#8CA3A7] mt-0.5">{monthOrders.length} order{monthOrders.length === 1 ? "" : "s"} in {monthLabel}</p>
          </div>
          <button data-testid="clear-month-btn" onClick={clearMonth} className="text-sm text-[#D95D39] font-medium hover:underline">Clear month filter</button>
        </div>
      )}

      {consol && (
        <div data-testid="consolidated-section" className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[#E6E4DD] bg-slate-50">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#8CA3A7]">Consolidated Outstanding</p>
              <h2 className="font-heading text-lg font-bold text-[#0A0D14] mt-0.5">
                By {consol.by === "buyer" ? "Buyer" : "Seller"} · {consol.groups.length} {consol.by === "buyer" ? "buyer" : "seller"}{consol.groups.length === 1 ? "" : "s"}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="consol-download-pdf-btn" onClick={downloadConsolidated} disabled={busy === "consoldl"} className="bg-[#1C2331] text-white rounded-sm px-3 py-2 text-xs font-medium flex items-center gap-1.5 hover:bg-[#2A3447] transition-colors disabled:opacity-60">
                <Download className="w-3.5 h-3.5" /> {busy === "consoldl" ? "Generating…" : "Download PDF"}
              </button>
              <button data-testid="consol-close-btn" onClick={() => setConsol(null)} className="text-sm text-[#8CA3A7] font-medium hover:text-[#991B1B]">Close</button>
            </div>
          </div>
          {consol.groups.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#4B5563]">No outstanding payments match the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
                    <th className="px-4 py-3">{consol.by === "buyer" ? "Seller" : "Buyer"}</th>
                    <th className="px-4 py-3 text-right">Payments Overdue</th>
                    <th className="px-4 py-3 text-right">Not Yet Due</th>
                    <th className="px-4 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {consol.groups.map((g) => (
                    <React.Fragment key={g.name}>
                      <tr className="bg-[#F6F5F2] border-b border-[#E6E4DD]">
                        <td colSpan={4} className="px-4 py-2 font-heading font-bold text-[#0A0D14]" data-testid={`consol-group-${g.name}`}>{g.name}</td>
                      </tr>
                      {g.rows.map((r) => (
                        <tr key={r.name} className="border-b border-slate-100" data-testid={`consol-row-${g.name}-${r.name}`}>
                          <td className="px-4 py-2.5 text-[#4B5563] pl-8">{r.name}</td>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums text-[#991B1B]">{fmtINR(r.overdue)}</td>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums text-[#4B5563]">{fmtINR(r.not_due)}</td>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums text-[#0A0D14] font-medium">{fmtINR(r.total)}</td>
                        </tr>
                      ))}
                      <tr className="border-b border-[#E6E4DD] bg-slate-50/60">
                        <td className="px-4 py-2 pl-8 text-xs uppercase tracking-wider text-[#8CA3A7] font-medium">Subtotal</td>
                        <td className="px-4 py-2 text-right font-mono tabular-nums text-[#991B1B] font-semibold">{fmtINR(g.subtotal.overdue)}</td>
                        <td className="px-4 py-2 text-right font-mono tabular-nums text-[#4B5563] font-semibold">{fmtINR(g.subtotal.not_due)}</td>
                        <td className="px-4 py-2 text-right font-mono tabular-nums text-[#0A0D14] font-semibold">{fmtINR(g.subtotal.total)}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#1C2331] text-white" data-testid="consol-grand-total">
                    <td className="px-4 py-3 font-heading font-bold uppercase tracking-wider text-xs">Grand Total</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-bold">{fmtINR(consol.grand.overdue)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-bold">{fmtINR(consol.grand.not_due)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-bold">{fmtINR(consol.grand.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}


      {filtered.length === 0 ? (
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-16 text-center">
          <Package className="w-8 h-8 text-[#8CA3A7] mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">No orders found.</p>
          <button onClick={() => navigate("/orders/new")} className="mt-4 text-sm text-[#D95D39] font-medium">Create your first order →</button>
        </div>
      ) : (
        <div className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
                <th className="px-4 py-3">PO</th>
                <th className="px-4 py-3">Fabric</th>
                <th className="px-4 py-3">Buyer / Seller</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-right">Value</th>
                <th className="px-4 py-3">Status</th>
                {canEdit && <th className="px-4 py-3">Close</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`order-row-${o.id}`}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="font-mono text-[#0A0D14]">{o.po_number || "—"}</div>
                    <div className="text-xs text-[#8CA3A7]" data-testid={`po-date-${o.id}`}>PO Date: {fmtDate(o.po_date) || "—"}</div>
                    <div className="text-xs text-[#4B5563] font-mono" data-testid={`po-rate-${o.id}`}>@ {fmtRate(o.rate_per_meter)}/m</div>
                    {o.po_number && (
                      <button
                        data-testid={`order-status-link-${o.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/status?po=${encodeURIComponent(o.po_number)}`); }}
                        className="text-[11px] text-[#D95D39] font-medium mt-0.5 inline-flex items-center gap-1 hover:underline"
                      >
                        <ClipboardList className="w-3 h-3" /> View status
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="text-xs text-[#0A0D14]" data-testid={`fabric-type-${o.id}`}>{o.fabric_type || "—"}</div>
                    {o.quality_details && (
                      <div className="text-xs text-[#8CA3A7] mt-0.5" data-testid={`fabric-quality-${o.id}`}>{o.quality_details}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[#0A0D14]">{o.buyer_name || "—"}</div>
                    <div className="text-xs text-[#8CA3A7]">{o.seller_name || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-[#4B5563]">
                    <div>{fmtMeters(o.delivered_quantity)}</div>
                    <div className="text-xs text-[#8CA3A7]">/ {fmtMeters(o.quantity_meters)}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-[#0A0D14]">
                    <div>{fmtINR(o.total_amount)}</div>
                    <div className="text-xs text-[#8CA3A7]">{fmtINR(o.outstanding_amount)} due</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 items-start">
                      {o.cancelled && <Pill status="cancelled" />}
                      <Pill status={o.delivery_status} />
                      <Pill status={o.overdue ? "unpaid" : o.payment_status} />
                    </div>
                  </td>
                  {canEdit && (
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col gap-1.5">
                      <label className="flex items-center gap-1.5 text-[11px] text-[#4B5563] cursor-pointer select-none">
                        <input type="checkbox" data-testid={`row-delivered-${o.id}`} checked={o.delivery_status === "delivered"} onChange={(e) => toggleClose(e, o, "delivery")} className="accent-[#065F46] w-3.5 h-3.5" />
                        Delivered
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] text-[#4B5563] cursor-pointer select-none">
                        <input type="checkbox" data-testid={`row-paid-${o.id}`} checked={o.payment_status === "paid"} onChange={(e) => toggleClose(e, o, "payment")} className="accent-[#065F46] w-3.5 h-3.5" />
                        Payment recd.
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] text-[#991B1B] cursor-pointer select-none">
                        <input type="checkbox" data-testid={`row-cancelled-${o.id}`} checked={!!o.cancelled} onChange={(e) => toggleClose(e, o, "cancel")} className="accent-[#991B1B] w-3.5 h-3.5" />
                        Cancelled
                      </label>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
